import { isValidElement } from "react";
import { modules } from "byzantine";
import Account from "byzantine/src/Account";
import type {
  WebSection,
  WebSectionId
} from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences";
import type App from "byzantine/src/App";
import type { InstitutionFeatures } from "byzantine/src/types";
import { renderSection } from "../Dashboard.sections";
import { SECTION_COLUMN_SIZES } from "../DashboardCustomization/constants";
import DashboardCustomization from "../DashboardCustomization/DashboardCustomization";
import { DashboardSectionContext } from "../types";
import { useUserFeatures } from "../../../components/contexts/UserFeaturesContext";
import NarmiDashboardTile from "../../../src/modules/narmiTiles/NarmiDashboardTile";

const spanClassName = (span: number) =>
  `dashboard-section dashboard-section--span-${span}`;

const DashboardSection = ({
  sectionId,
  sectionContext
}: {
  sectionId: WebSectionId;
  sectionContext: DashboardSectionContext;
}) => {
  const tile = modules.narmiTiles.useSectionTile(sectionId);
  const { aoiob } = useUserFeatures() as InstitutionFeatures;
  const visibleServices = sectionContext.servicesApps ?? [];

  const servicesCount = visibleServices.length + (aoiob ? 1 : 0);
  const isServicesTwoColumn = sectionId === "services" && servicesCount > 4;
  const defaultSpan = SECTION_COLUMN_SIZES[sectionId] ?? 1;

  if (tile) {
    return (
      <div className={spanClassName(isServicesTwoColumn ? 2 : defaultSpan)}>
        <NarmiDashboardTile tile={tile} isFixed />
      </div>
    );
  }

  // chart_card can render the net worth widget alongside the Spending &
  // Income widget (PfmContainer) — they're independent, so the extra width
  // the net worth widget's chart + monthly totals list needs to sit side by
  // side must be scoped to its own node, not applied to the whole section.
  return (
    <>
      {renderSection(sectionId, sectionContext).map((node) => {
        const isNetWorthWidgetNode =
          isValidElement(node) && node.key === "net_worth_widget";
        const isTwoColumn = isServicesTwoColumn || isNetWorthWidgetNode;
        return (
          <div
            key={isValidElement(node) ? node.key : undefined}
            className={spanClassName(isTwoColumn ? 2 : defaultSpan)}
          >
            {node}
          </div>
        );
      })}
    </>
  );
};

const LargeViewportLayout = ({
  sections,
  externalAccounts,
  servicesApps,
  sectionContext
}: {
  sections: WebSection[];
  externalAccounts: Account[];
  servicesApps: App[];
  sectionContext: DashboardSectionContext;
}) => (
  <>
    {sections.map(({ section_id: sectionId }) => (
      <DashboardSection
        key={sectionId}
        sectionId={sectionId}
        sectionContext={sectionContext}
      />
    ))}
    <div className="dashboard-section--span-2">
      <DashboardCustomization
        externalAccounts={externalAccounts}
        servicesApps={servicesApps}
      />
    </div>
  </>
);

export default LargeViewportLayout;
